import { supabase } from '../lib/supabase'
import { sanitizeText } from '../utils/sanitizer'
import Sentry from '../lib/sentry'

// Create comment
export const createComment = async (commentData) => {
  try {
    // Sanitize inputs
    const cleanData = {
      ...commentData,
      content: sanitizeText(commentData.content),
      client_name: commentData.client_name ? sanitizeText(commentData.client_name) : null,
    }

    const { data, error } = await supabase
      .from('comments')
      .insert([cleanData])
      .select(`
        *,
        user:users(email, full_name)
      `)
      .single()

    if (error) throw error
    return data
  } catch (error) {
    Sentry.captureException(error, {
      context: { function: 'createComment', versionId: commentData.version_id }
    })
    throw error
  }
}

// Get comments for version with pagination
export const getVersionComments = async (versionId, offset = 0, limit = 20) => {
  try {
    // Get total count
    const { count } = await supabase
      .from('comments')
      .select('*', { count: 'exact', head: true })
      .eq('version_id', versionId)

    // Get page data
    const { data, error } = await supabase
      .from('comments')
      .select(`
        *,
        user:users(email, full_name)
      `)
      .eq('version_id', versionId)
      .order('created_at', { ascending: true })
      .range(offset, offset + limit - 1)

    if (error) throw error
    
    return {
      data,
      total: count
    }
  } catch (error) {
    Sentry.captureException(error, {
      context: { function: 'getVersionComments', versionId }
    })
    throw error
  }
}

// Create approval
export const createApproval = async (approvalData, metadata = {}) => {
  // Extract metadata fields to dedicated columns for legal compliance
  const { client_email, ip_address, user_agent, ...otherMetadata } = metadata
  
  const { data: approval, error: approvalError } = await supabase
    .from('approvals')
    .insert([{
      ...approvalData,
      client_email,      // Extract from metadata to column
      ip_address,        // Extract from metadata to column
      user_agent,        // Extract from metadata to column
      approved_at: new Date().toISOString(),
      metadata: otherMetadata // Store remaining metadata
    }])
    .select()
    .single()

  if (approvalError) throw approvalError

  // Create audit log
  await createAuditLog({
    action_type: approvalData.status,
    project_id: approvalData.project_id,
    version_id: approvalData.version_id,
    user_id: approvalData.user_id,
    ip_address,
    user_agent,
    metadata: {
      ...metadata,
      approval_id: approval.id,
      status: approvalData.status,
      comment: approvalData.comment
    }
  })

  return approval
}

// Request changes
export const requestChanges = async (versionId, userId, comment, metadata = {}) => {
  // Get project_id from version
  const { data: version, error: versionError } = await supabase
    .from('project_versions')
    .select('project_id')
    .eq('id', versionId)
    .single()
  
  if (versionError) throw versionError

  const approval = await createApproval({
    version_id: versionId,
    project_id: version.project_id, // Include project_id (required)
    user_id: userId,
    status: 'changes_requested',
    comment
  }, metadata)

  // Update project status
  await supabase
    .from('projects')
    .update({ status: 'changes_requested' })
    .eq('id', version.project_id)

  return approval
}

// Approve version
export const approveVersion = async (versionId, userId, comment, metadata = {}) => {
  // Get project_id from version
  const { data: version, error: versionError } = await supabase
    .from('project_versions')
    .select('project_id')
    .eq('id', versionId)
    .single()
  
  if (versionError) throw versionError

  const approval = await createApproval({
    version_id: versionId,
    project_id: version.project_id, // Include project_id (required)
    user_id: userId,
    status: 'approved',
    comment
  }, metadata)

  // Update project status
  await supabase
    .from('projects')
    .update({ status: 'approved' })
    .eq('id', version.project_id)

  return approval
}

// Create audit log entry
export const createAuditLog = async (logData) => {
  const { data, error } = await supabase
    .from('audit_logs')
    .insert([{
      ...logData,
      timestamp: new Date().toISOString()
    }])
    .select()
    .single()

  if (error) throw error
  return data
}

// Get audit logs for project
export const getProjectAuditLogs = async (projectId) => {
  const { data, error } = await supabase
    .from('audit_logs')
    .select(`
      *,
      user:users(email, full_name)
    `)
    .eq('project_id', projectId)
    .order('timestamp', { ascending: false })

  if (error) throw error
  return data
}

// Get approval status for version
export const getVersionApprovals = async (versionId) => {
  const { data, error } = await supabase
    .from('approvals')
    .select(`
      *,
      user:users(email, full_name)
    `)
    .eq('version_id', versionId)
    .order('approved_at', { ascending: false })

  if (error) throw error
  return data
}
